import { HTMLAttributes, useContext, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Drawer, Input, message } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { get } from 'lodash-es';
import dayjs from 'lib/dayjs';
import useModal from 'hooks/useModal';
import toLowerCase from 'utils/toLowerCase';
import Empty from 'components/Empty';
import TransComponent from 'components/TransComponent';
import { exportReview } from 'services/api/review';
import { useGlobalReview, useReview } from './hooks/useNewReview';
import ProfileList from './components/ProfileList';
import { ReviewContext } from './Context';
import Sorter from './components/Sorter';
import NewFilter, { isDefaultFilte } from './components/NewFilter';
import { defaultCursor, getDefaultFilte, resetCursor } from './hooks/useSearch';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { getHeaderLang } from 'services/api';
import { T, useTranslate } from '@tolgee/react';
import { PlatformTabs } from './PlatformTabs';
import { TikTokStoreStatus } from './components/TikTokStoreStatus';
import { isTiKTok } from 'lib/utils/isTikTok';
import { zIndex } from 'services/config/styles';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: 100%;
  background: #fff;
  .${({ theme }) => theme.getAntdPrefixedClassName('drawer-content-wrapper')} {
    box-shadow: none !important;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  padding: 8px;
  border-bottom: solid 1px #f0f2f5;
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  margin: -24px;
  height: calc(100% + 48px);
`;

const ListWrapper = styled.div`
  flex: 1 0 0;
  overflow: hidden;
  > div {
    height: 100%;
  }
`;

const platformTabsHeight = 48;

const Toolbar = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  padding: 0 4px;
  border-bottom: solid 1px #f0f2f5;
`;

const MissingBanner = styled.div`
  padding: 8px 22px;
  background: #faedd2;
  text-align: center;
  > button {
    height: 22px;
    padding: 0 4px;
  }
`;

function List(props: HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslate();

  const { visible, show, hide } = useModal();

  const [filterVisible, setFilterVisible] = useState(false);

  const [keywords, setKeywords] = useState('');

  const keywordsRef = useRef('');

  const { list, cursor, hasMore, setList, setCursor, setHasMore, fetchList } = useReview();

  const {
    currentReview,
    reviewList: { list: globalList, hasMore: globalHasMore, loading: globalLoading },
    fetchList: fetchGlobalList,
    updateStatus,
    shopeeSearch,
    lazadaSearch,
    mercadoSearch,
    setShopeeSearch,
    setLazadaSearch,
    setMercadoSearch,
    platformInfo: { currentPlatform: platform, displayPlatformTab },
    setPlatformInfo,
    resetGlobalList,
    changeFilte,
    missingReviewListMap,
    reviewStat,
    mercadoEnable,
    tiktokSearch,
    tiktokUSSearch,
    setTikTokSearch,
    setTikTokUSSearch,
  } = useGlobalReview();

  const platformRef = useRef(platform);

  const contextValue = useContext(ReviewContext);

  const { run: onDownload } = useExportFile();

  const filteredGlobalList = useMemo(() => {
    if (!platform) {
      return globalList;
    }

    const { handleStatus: searchStatus } = {
      SHOPEE: shopeeSearch,
      LAZADA: lazadaSearch,
      MERCADO: mercadoSearch,
      TIKTOK: tiktokSearch,
      TIKTOK_US: tiktokUSSearch,
    }[platform];

    return globalList.filter((profile) => {
      if (currentReview && profile.commentId === currentReview.commentId) {
        return true;
      }

      if (!searchStatus || searchStatus.length === 0) {
        return true;
      }

      if (searchStatus.some((status) => status === `${profile.status}`)) {
        return true;
      }

      return false;
    });
  }, [
    globalList,
    currentReview,
    shopeeSearch,
    lazadaSearch,
    mercadoSearch,
    platform,
    tiktokSearch,
    tiktokUSSearch,
  ]);

  const lang = useSystemLanguageValue();

  if (!platform) {
    return <LoadingOutlined />;
  }

  const searchConfig = {
    SHOPEE: shopeeSearch,
    LAZADA: lazadaSearch,
    MERCADO: mercadoSearch,
    TIKTOK: tiktokSearch,
    TIKTOK_US: tiktokUSSearch,
  }[platform];

  const { size, cursor: globalCursor, ...filte } = searchConfig;

  const { [platform]: missingReviewList } = missingReviewListMap;

  const search = () => {
    if (!keywords || (keywords === keywordsRef.current && platform === platformRef.current)) {
      return;
    }

    keywordsRef.current = keywords;
    platformRef.current = platform;

    show();
    setList([]);
    setCursor(defaultCursor);
    setHasMore(true);
  };

  const input = (
    <InputWrapper>
      <Input
        placeholder={t('review.请输入订单号搜索')}
        allowClear
        value={keywords}
        onChange={({ target: { value } }) => setKeywords(value)}
        suffix={<SearchOutlined onClick={search} />}
        onPressEnter={search}
      />
      {visible && (
        <Button
          type="link"
          onClick={() => {
            hide();
            setKeywords('');
            keywordsRef.current = '';
          }}
        >
          {t('phrase.返回')}
        </Button>
      )}
    </InputWrapper>
  );

  const onStatusChange = (profile: Review.Profile, status: Review.Status) => {
    return updateStatus({
      commentId: profile.commentId,
      status,
    })
      .then(() => {
        setList((cur) => {
          return cur.map((review) => {
            if (review.commentId === profile.commentId) {
              return {
                ...review,
                status,
              };
            }

            return review;
          });
        });

        // 更新的评价为当前评价
        if (contextValue?.profile.commentId === profile.commentId) {
          return contextValue?.refreshReview();
        }

        return Promise.resolve();
      })
      .then(() => {
        message.success(t('review.评价更新成功'));
      })
      .catch((err) => {
        if (get(err, 'response.status') === 409) {
          message.warn(t('review评价已被处理，请去处理其他评价吧'));
        } else {
          message.error(t('review更新评价状态失败，请稍后再试'));
        }
      });
  };

  return (
    <Container {...props}>
      {displayPlatformTab && (
        <PlatformTabs
          platform={platform}
          mercadoEnable={mercadoEnable}
          reviewStat={reviewStat}
          onChange={(type) => {
            setPlatformInfo((info) => {
              return {
                ...info,
                currentPlatform: type as Review.Platform,
              };
            });

            resetGlobalList(true);
          }}
        />
      )}
      {isTiKTok(platform) && <TikTokStoreStatus platform={platform} />}
      {input}
      <Toolbar>
        <NewFilter
          key={platform}
          filte={filte}
          onFilteChange={changeFilte}
          visible={filterVisible}
          onVisibleChange={setFilterVisible}
          onExport={(value) => {
            const params = {
              ...value,
              cursor: [
                // 按照评价时间倒序导出
                {
                  fieldName: 'comment_time',
                  direction: 'ASC',
                },
              ],
              // 最大导出数量
              size: 10000,
              lang: getHeaderLang(lang),
            };
            onDownload(() => exportReview(params));
          }}
        />
        <Sorter
          platform={platform}
          cursor={searchConfig.cursor}
          onCursorChange={(cursor) => {
            if (platform === 'SHOPEE') {
              setShopeeSearch(resetCursor({ ...shopeeSearch, cursor }));
            }

            if (platform === 'LAZADA') {
              setLazadaSearch(resetCursor({ ...lazadaSearch, cursor }));
            }

            if (platform === 'MERCADO') {
              setMercadoSearch(resetCursor({ ...mercadoSearch, cursor }));
            }
            if (platform === 'TIKTOK') {
              setTikTokSearch(resetCursor({ ...tiktokSearch, cursor }));
            }
            if (platform === 'TIKTOK_US') {
              setTikTokUSSearch(resetCursor({ ...tiktokUSSearch, cursor }));
            }

            resetGlobalList();
          }}
        />
      </Toolbar>
      {missingReviewList.length > 0 && (
        <MissingBanner>
          <T
            keyName="review.当前有x条新中差评价进入，避免遗漏中差评请及时处理，建议action筛选"
            params={{
              x: missingReviewList.length,
              action: (
                <TransComponent
                  content={
                    <Button
                      type="link"
                      onClick={() => changeFilte(getDefaultFilte(searchConfig.platform))}
                    >
                      恢复默认推荐
                    </Button>
                  }
                />
              ),
            }}
          />
        </MissingBanner>
      )}
      <ListWrapper>
        <ProfileList
          isGlobal
          list={filteredGlobalList}
          hasMore={globalHasMore}
          loading={globalLoading}
          onStatusChange={onStatusChange}
          onLoadMore={() => {
            return fetchGlobalList({
              ...searchConfig,
              reviewStartTime:
                searchConfig.reviewStartTime ||
                dayjs().subtract(30, 'days').startOf('day').valueOf().toString(),
              reviewEndTime:
                searchConfig.reviewEndTime || dayjs().endOf('days').valueOf().toString(),
            });
          }}
          emptyRender={() => (
            <Empty
              description={
                <>
                  <div>{t('review.当前筛选条件中无符合条件的数据')}</div>
                  <div>
                    <T
                      keyName="review.建议action1或action2"
                      params={{
                        action1: (
                          <Button type="link" onClick={() => setFilterVisible(true)}>
                            {t('review.重新筛选')}
                          </Button>
                        ),
                        action2: isDefaultFilte(filte) ? (
                          <Button type="link" onClick={() => setFilterVisible(true)}>
                            {t('review.增加筛选时间')}
                          </Button>
                        ) : (
                          <Button
                            type="link"
                            onClick={() => changeFilte(getDefaultFilte(searchConfig.platform))}
                          >
                            {t('review.恢复默认推荐')}
                          </Button>
                        ),
                      }}
                    />
                  </div>
                </>
              }
            />
          )}
        />
      </ListWrapper>
      <Drawer
        visible={visible}
        mask={false}
        closable={false}
        placement="bottom"
        height={`calc(100% - ${displayPlatformTab ? platformTabsHeight : 0}px)`}
        style={{
          position: 'absolute',
        }}
        contentWrapperStyle={{
          transition: 'none',
        }}
        getContainer={false}
        zIndex={zIndex.low}
      >
        <DrawerContent>
          {input}
          <ListWrapper>
            <ProfileList
              list={list}
              hasMore={hasMore}
              onStatusChange={onStatusChange}
              onLoadMore={() => {
                return fetchList({
                  platform: toLowerCase(platform),
                  hasContent: 'all',
                  orderId: keywords,
                  size: 10,
                  cursor,
                });
              }}
              emptyRender={() => {
                return <Empty description={t('订单不存在或暂无评价')} />;
              }}
            />
          </ListWrapper>
        </DrawerContent>
      </Drawer>
    </Container>
  );
}

export default List;
